import React, { useLayoutEffect, useRef } from 'react';
import { createPopper } from '@popperjs/core';
import type { StrictModifiers, Instance } from '@popperjs/core';
import Portal from '../Portal';

export enum Placement {
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
}

interface PopperProps {
  open: Boolean;
  style?: React.CSSProperties;
  modifiers: StrictModifiers[];
  placement: Placement;
  targetRef?: HTMLElement;
  arrowRef?: HTMLDivElement | null;
  children: React.ReactElement<React.PropsWithChildren<{}>>;
  transition?: boolean;
}

export default function Popper({
  open,
  style,
  modifiers = [],
  placement = Placement.Bottom,
  targetRef,
  arrowRef,
  children,
  transition = false,
}: PopperProps) {
  const popperRef: { current: HTMLElement | null } = useRef<HTMLElement>(null);
  const popperInstanceRef: { current: Instance | null } = useRef<Instance>(null);
  const handleOpen = React.useCallback(() => {
    if (!popperRef.current || !targetRef || !open) {
      return;
    }
    popperInstanceRef.current?.destroy();
    const popper = createPopper(targetRef, popperRef.current, {
      placement,
      modifiers: [...modifiers, { name: 'arrow', options: { enabled: Boolean(arrowRef), element: arrowRef } }],
    });
    (popperInstanceRef as React.MutableRefObject<{}>).current = popper;
  }, [arrowRef, modifiers, open, placement, targetRef]);

  const handleRef = React.useCallback(
    (node: HTMLElement) => {
      popperRef.current = node;
      handleOpen();
    },
    [handleOpen],
  );

  useLayoutEffect(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.forceUpdate();
    }
  });
  if (!open) {
    return null;
  }
  const childrenProps = {
    ...children?.props,
    ref: handleRef,
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      display: !open && !transition ? 'none' : null,
      ...style,
    },
  };
  return <Portal>{React.cloneElement(children, childrenProps)}</Portal>;
}
